import { apiRequest, useDevelopmentFixtures } from "../api/client";

export type CrmService = "balloon" | "tour" | "school";
export type CrmStage = "new" | "conversation" | "quote" | "awaiting" | "won" | "lost";

export interface CrmPriority {
  id: string;
  customerId: string;
  customerName: string;
  opportunityId: string;
  service: CrmService;
  stage: CrmStage;
  dueAt: string;
  dueLabel: string;
  note: string;
  channel: "whatsapp" | "web" | "manual";
}

export interface CrmOperation {
  service: CrmService;
  time: string;
  reservations: number;
  passengers: number;
}

export interface CrmAlert {
  id: string;
  label: string;
  detail: string;
}

export interface CrmToday {
  summary: { overdueFollowups: number; newOpportunities: number; pendingQuotes: number; servicesToday: number };
  priorities: CrmPriority[];
  operations: CrmOperation[];
  alerts: CrmAlert[];
}

export interface CrmInboxItem {
  id: string;
  customerId: string;
  customerName: string;
  channel: "whatsapp" | "web" | "manual";
  state: "new" | "open" | "waiting" | "closed";
  subject: string;
  preview: string;
  lastActivityAt: string;
  version: number;
  opportunityId: string | null;
  service: CrmService | null;
  stage: CrmStage | null;
}

export interface CrmOpportunity {
  id: string;
  customerId: string;
  customerName: string;
  threadId: string | null;
  service: CrmService;
  stage: CrmStage;
  requestedDate: string | null;
  peopleCount: number | null;
  estimatedTotal: string | null;
  currency: string;
  nextFollowUpAt: string | null;
  lastActivityAt: string;
  lostReason: string | null;
  wonSource?: "manual" | "wordpress" | null;
  wonAt?: string | null;
  version: number;
  channel: "whatsapp" | "web" | "manual" | null;
}

export interface CrmCustomerDetail {
  customer: { id: string; displayName: string; language: "es" | "en"; sourceChannel: "whatsapp" | "web" | "manual"; createdAt: string; updatedAt: string; version: number };
  identities: Array<{ id: string; customerId: string; type: "phone" | "email"; value: string; normalizedValue: string; verifiedAt: string | null; isPrimary: boolean }>;
  opportunities: CrmOpportunity[];
  activities: Array<{ id: string; customerId: string; opportunityId: string | null; type: string; body: string; actorLabel: string | null; metadata: Record<string, unknown>; occurredAt: string }>;
  followups: Array<{ id: string; opportunityId: string; dueAt: string; note: string; status: "pending" | "completed" | "cancelled"; completedAt: string | null; version: number }>;
  drafts: Array<{ id: string; threadId: string; opportunityId: string | null; followupId: string | null; body: string; status: "draft" | "sent" | "discarded"; createdBy: string; sentAt: string | null; version: number }>;
  reservations: Array<{ id: string; sourceId: string; status: string; service: string; bookingDate: string; bookingTime: string; peopleCount: number; total: string | null; currency: string | null; orderNumber: string | null; paymentStatus: string | null; isPaid: boolean }>;
  merge: { id?: string; mergedAt?: string; undoExpiresAt?: string; version?: number; canUndo: boolean };
}

export interface CrmCustomer {
  organization?: string|null;
  id: string;
  displayName: string;
  language: "es" | "en";
  sourceChannel: "whatsapp" | "web" | "manual";
  phoneE164: string | null;
  email: string | null;
  openOpportunities: number;
  totalOpportunities: number;
  updatedAt: string;
  version: number;
}

interface CrmListResponse<T> {
  data: T[];
  pagination: { page: number; perPage: number; total: number };
  summary?: { pending:number;waiting:number;all:number };
}

export const crmTodayFixture: CrmToday = {
  summary: { overdueFollowups: 3, newOpportunities: 4, pendingQuotes: 2, servicesToday: 5 },
  priorities: [
    { id: "followup-maria", customerId: "customer-maria", customerName: "María González", opportunityId: "opportunity-maria", service: "balloon", stage: "quote", dueAt: "2026-09-01T14:00:00.000Z", dueLabel: "2 h", channel: "web", note: "Cotización pendiente · seguimiento vencido hace 2 h" },
    { id: "followup-horizonte", customerId: "customer-horizonte", customerName: "Colegio Horizonte", opportunityId: "opportunity-horizonte", service: "school", stage: "conversation", dueAt: "2026-09-01T13:00:00.000Z", dueLabel: "12:00", channel: "web", note: "Fecha tentativa sin confirmar · último contacto ayer" },
    { id: "followup-daniel", customerId: "customer-daniel", customerName: "Daniel Ruiz", opportunityId: "opportunity-daniel", service: "tour", stage: "awaiting", dueAt: "2026-09-01T22:00:00.000Z", dueLabel: "16:00", channel: "whatsapp", note: "Esperando decisión · revisar hoy a las 16:00" },
  ],
  operations: [
    { service: "balloon", time: "05:30", reservations: 3, passengers: 8 },
    { service: "tour", time: "09:00", reservations: 1, passengers: 4 },
  ],
  alerts: [{ id: "confirmation-email-failed", label: "Atención", detail: "1 confirmación de correo fallida" }],
};

const crmInboxFixture: CrmInboxItem[] = crmTodayFixture.priorities.map((priority) => ({
  id: priority.id,
  customerId: priority.customerId,
  customerName: priority.customerName,
  channel: priority.channel,
  state: "open",
  subject: priority.note,
  preview: priority.note,
  lastActivityAt: priority.dueAt,
  version: 1,
  opportunityId: priority.opportunityId,
  service: priority.service,
  stage: priority.stage,
}));

const crmOpportunityFixture: CrmOpportunity[] = crmTodayFixture.priorities.map((priority) => ({
  id: priority.opportunityId,
  customerId: priority.customerId,
  customerName: priority.customerName,
  threadId: priority.id,
  service: priority.service,
  stage: priority.stage,
  requestedDate: priority.service === "school" ? null : "2026-10-16",
  peopleCount: priority.service === "school" ? 45 : 4,
  estimatedTotal: null,
  currency: "MXN",
  nextFollowUpAt: priority.dueAt,
  lastActivityAt: priority.dueAt,
  lostReason: null,
  version: 1,
  channel: priority.channel,
}));

const crmCustomerFixture: CrmCustomer[] = crmTodayFixture.priorities.map((priority) => ({
  id: priority.customerId,
  displayName: priority.customerName,
  language: "es",
  sourceChannel: priority.channel,
  phoneE164: null,
  email: null,
  openOpportunities: 1,
  totalOpportunities: 1,
  updatedAt: priority.dueAt,
  version: 1,
}));

const fixtureDetails = new Map<string, CrmCustomerDetail>();
function detailFixture(customerId: string): CrmCustomerDetail {
  const existing = fixtureDetails.get(customerId);
  if (existing) return existing;
  const customer = crmCustomerFixture.find((item) => item.id === customerId) ?? crmCustomerFixture[0]!;
  const opportunities = crmOpportunityFixture.filter((item) => item.customerId === customer.id);
  const detail: CrmCustomerDetail = {
    customer: { id: customer.id, displayName: customer.displayName, language: customer.language,
      sourceChannel: customer.sourceChannel, createdAt: customer.updatedAt, updatedAt: customer.updatedAt, version: customer.version },
    identities: [{ id: `phone-${customer.id}`, customerId: customer.id, type: "phone", value: "+52 55 0000 0002", normalizedValue: "+525500000002", verifiedAt: customer.updatedAt, isPrimary: true }],
    opportunities,
    activities: [{ id: `activity-${customer.id}`, customerId: customer.id, opportunityId: opportunities[0]?.id ?? null,
      type: "contact_received", body: customer.id === "customer-horizonte" ? "Somos un grupo de primaria, aproximadamente 45 alumnos. Nos gustaría conocer los talleres y organizar una visita." : customer.id === "customer-daniel" ? "Gracias por la información. Voy a revisar la propuesta del tour con mi familia." : "Me gustaría revisar los detalles de nuestra reserva de vuelo en globo.", actorLabel: "Web", metadata: {}, occurredAt: customer.updatedAt }],
    followups: opportunities[0] ? [{ id: `followup-${customer.id}`, opportunityId: opportunities[0].id, dueAt: opportunities[0].nextFollowUpAt ?? customer.updatedAt,
      note: "Confirmar disponibilidad y resolver dudas", status: "pending", completedAt: null, version: 1 }] : [],
    drafts: [],
    reservations: customer.id === "customer-maria" ? [{
      id: "booking-maria",
      sourceId: "booking-524",
      status: "confirmed",
      service: "balloon",
      bookingDate: "2026-09-05",
      bookingTime: "05:30",
      peopleCount: 4,
      total: "9200.00",
      currency: "MXN",
      orderNumber: "PED-DEMO-001",
      paymentStatus: "processing",
      isPaid: true,
    }] : [],
    merge: { canUndo: false },
  };
  fixtureDetails.set(customerId, detail);
  return detail;
}

export const crmApi = {
  today: async (): Promise<CrmToday> => {
    if (useDevelopmentFixtures) return structuredClone(crmTodayFixture);
    return (await apiRequest<{ data: CrmToday }>("/api/v1/crm/today")).data;
  },
  createContact: async (input: {
    display_name: string; phone_e164: string; channel: "manual"; language: "es";
    commercial_intent: true; service_type: CrmService; note?: string;
    requested_date?: string; requested_date_precision?: "day" | "month"; people_count?: number; whatsapp_conversation_id?: string;
  }): Promise<{ customerId: string; threadId: string; opportunityId: string | null }> => {
    if (useDevelopmentFixtures) {
      const customerId = crypto.randomUUID(), threadId = crypto.randomUUID(), opportunityId = crypto.randomUUID(), now = new Date().toISOString();
      crmCustomerFixture.unshift({ id: customerId, displayName: input.display_name, language: "es", sourceChannel: "manual", phoneE164: input.phone_e164, email: null, openOpportunities: 1, totalOpportunities: 1, updatedAt: now, version: 1 });
      crmOpportunityFixture.unshift({ id: opportunityId, customerId, customerName: input.display_name, threadId, service: input.service_type, stage: "new", requestedDate: null, peopleCount: null, estimatedTotal: null, currency: "MXN", nextFollowUpAt: null, lastActivityAt: now, lostReason: null, version: 1, channel: "manual" });
      crmInboxFixture.unshift({ id: threadId, customerId, customerName: input.display_name, channel: "manual", state: "new", subject: input.note ?? "Contacto nuevo", preview: input.note ?? "Contacto nuevo", lastActivityAt: now, version: 1, opportunityId, service: input.service_type, stage: "new" });
      const detail = detailFixture(customerId);
      detail.identities[0]!.value = input.phone_e164; detail.identities[0]!.normalizedValue = input.phone_e164;
      detail.activities[0]!.body = input.note ?? "Contacto creado manualmente.";
      detail.followups = [];
      return { customerId, threadId, opportunityId };
    }
    return (await apiRequest<{ data: { customerId: string; threadId: string; opportunityId: string | null } }>(
      "/api/v1/crm/contacts",
      { method: "POST", headers: { "Idempotency-Key": crypto.randomUUID() }, body: JSON.stringify(input) },
    )).data;
  },
  listInbox: async (search = "", filters: { service?: CrmService | "all"; stage?: CrmStage; page?: number } = {}): Promise<CrmListResponse<CrmInboxItem>> => {
    const page = Math.max(1, Math.floor(filters.page ?? 1));
    if (useDevelopmentFixtures) {
      const normalized = search.trim().toLocaleLowerCase("es-MX");
      const matches = crmInboxFixture.filter((item) => (!filters.service || filters.service === "all" || item.service === filters.service)
        && (!filters.stage || item.stage === filters.stage)
        && (!normalized || `${item.customerName} ${item.subject} ${item.preview}`.toLocaleLowerCase("es-MX").includes(normalized)));
      return { data: structuredClone(matches.slice((page - 1) * 100, page * 100)), pagination: { page, perPage: 100, total: matches.length } };
    }
    const query = new URLSearchParams({ page: String(page), per_page: "100" });
    if (search.trim()) query.set("search", search.trim());
    if (filters.service && filters.service !== "all") query.set("service_type", filters.service);
    if (filters.stage) query.set("stage", filters.stage);
    return apiRequest<CrmListResponse<CrmInboxItem>>(`/api/v1/crm/inbox?${query}`);
  },
  listOpportunities: async (page = 1, filters: {stage?:CrmStage;attention?:"active"|"waiting"|"all";service?:CrmService|"all";search?:string} = {}): Promise<CrmListResponse<CrmOpportunity>> => {
    if (useDevelopmentFixtures) {
      const matches=crmOpportunityFixture.filter(item => (!filters.stage||item.stage===filters.stage)&&(!filters.service||filters.service==="all"||item.service===filters.service)&&(!filters.search||item.customerName.toLocaleLowerCase("es").includes(filters.search.toLocaleLowerCase("es"))));
      const waiting=(item:CrmOpportunity)=>Boolean(item.nextFollowUpAt&&new Date(item.nextFollowUpAt).getTime()>Date.now());
      const open=(item:CrmOpportunity)=>item.stage!=="won"&&item.stage!=="lost";
      const data=matches.filter(item=>!filters.attention||filters.attention==="all"||(open(item)&&(filters.attention==="waiting"?waiting(item):!waiting(item)))).sort((a,b)=>Number(!open(a))-Number(!open(b)) || (a.nextFollowUpAt ? new Date(a.nextFollowUpAt).getTime() : 0)-(b.nextFollowUpAt ? new Date(b.nextFollowUpAt).getTime() : 0) || b.lastActivityAt.localeCompare(a.lastActivityAt));
      return {data:structuredClone(data),pagination:{page:1,perPage:100,total:data.length},summary:{pending:matches.filter(item=>open(item)&&!waiting(item)).length,waiting:matches.filter(item=>open(item)&&waiting(item)).length,all:matches.length}};
    }
    const query=new URLSearchParams({page:String(page),per_page:"100"});
    if(filters.attention)query.set("attention",filters.attention);
    if(filters.stage)query.set("stage",filters.stage);
    if(filters.service&&filters.service!=="all")query.set("service_type",filters.service);
    if(filters.search?.trim())query.set("search",filters.search.trim());
    return apiRequest<CrmListResponse<CrmOpportunity>>(`/api/v1/crm/opportunities?${query}`);
  },
  listCustomers: async (search = "", requestedPage = 1): Promise<CrmListResponse<CrmCustomer>> => {
    const page = Math.max(1, Math.floor(requestedPage));
    if (useDevelopmentFixtures) {
      const normalized = search.trim().toLocaleLowerCase("es-MX");
      const matches = crmCustomerFixture.filter((item) => !normalized || `${item.displayName} ${item.phoneE164 ?? ""} ${item.email ?? ""}`.toLocaleLowerCase("es-MX").includes(normalized));
      return { data: structuredClone(matches.slice((page - 1) * 30, page * 30)), pagination: { page, perPage: 30, total: matches.length } };
    }
    const query = new URLSearchParams({ page: String(page), per_page: "30" });
    if (search.trim()) query.set("search", search.trim());
    return apiRequest<CrmListResponse<CrmCustomer>>(`/api/v1/crm/customers?${query}`);
  },
  customerDetail: async (customerId: string): Promise<CrmCustomerDetail> => {
    if (useDevelopmentFixtures) return structuredClone(detailFixture(customerId));
    return (await apiRequest<{ data: CrmCustomerDetail }>(`/api/v1/crm/customers/${customerId}`)).data;
  },
  updateCustomer: async (customerId: string, input: { expected_version: number; display_name: string; language: "es" | "en"; phone_e164: string | null; email: string | null }) => {
    if (useDevelopmentFixtures) {
      const detail = detailFixture(customerId);
      Object.assign(detail.customer, { displayName: input.display_name, language: input.language, version: input.expected_version + 1 });
      crmOpportunityFixture.filter(item => item.customerId === customerId).forEach(item => { item.customerName = input.display_name; });
      const customer = crmCustomerFixture.find(item => item.id === customerId);
      if (customer) Object.assign(customer, { displayName: input.display_name, phoneE164: input.phone_e164, email: input.email, version: input.expected_version + 1 });
      detail.identities = ([ ["phone", input.phone_e164], ["email", input.email] ] as const).filter((entry): entry is readonly ["phone" | "email", string] => Boolean(entry[1])).map(([type, value]) => ({ id: `${type}-${customerId}`, customerId, type, value, normalizedValue: value, verifiedAt: null, isPrimary: true }));
      return detail.customer;
    }
    return (await apiRequest<{ data: CrmCustomerDetail["customer"] }>(`/api/v1/crm/customers/${customerId}`, { method: "PATCH", body: JSON.stringify(input) })).data;
  },
  addNote: async (customerId: string, body: string, opportunityId?: string) => {
    if (useDevelopmentFixtures) {
      const entry = { id: crypto.randomUUID(), customerId, opportunityId: opportunityId ?? null, type: "note", body, actorLabel: "Equipo · demo", metadata: {}, occurredAt: new Date().toISOString() };
      detailFixture(customerId).activities.unshift(entry);
      return entry;
    }
    return (await apiRequest<{ data: Record<string, unknown> }>(`/api/v1/crm/customers/${customerId}/notes`, { method: "POST", body: JSON.stringify({ body, opportunity_id: opportunityId ?? null }) })).data;
  },
  /** Trip facts edited from the case header; the version guards against overwriting newer data. */
  updateOpportunityDetails: async (opportunityId: string, input: { expected_version: number; requested_date?: string | null; requested_date_precision?: "day" | "month"; people_count?: number | null; teachers_count?: number | null; companions_count?: number | null; service_type?: "balloon" | "tour" | "school" }) => {
    if (useDevelopmentFixtures) {
      const opportunity = crmOpportunityFixture.find((item) => item.id === opportunityId);
      if (!opportunity) throw new Error("Oportunidad no encontrada.");
      if ("requested_date" in input) opportunity.requestedDate = input.requested_date ?? null;
      if ("people_count" in input) opportunity.peopleCount = input.people_count ?? null;
      if (input.service_type) opportunity.service = input.service_type;
      opportunity.version += 1;
      return { id: opportunityId, version: opportunity.version };
    }
    return (await apiRequest<{ data: Record<string, unknown> }>(`/api/v1/crm/opportunities/${opportunityId}/details`, { method: "PATCH", body: JSON.stringify(input) })).data;
  },
  /** Forward-only stage step (never back, never won/lost). */
  advanceOpportunity: async (opportunityId: string, stage: "quote" | "awaiting", reason: string) => {
    if (useDevelopmentFixtures) {
      const opportunity = crmOpportunityFixture.find((item) => item.id === opportunityId);
      const order = ["new", "conversation", "quote", "awaiting"];
      if (opportunity && order.indexOf(opportunity.stage) >= 0 && order.indexOf(opportunity.stage) < order.indexOf(stage)) opportunity.stage = stage;
      return { moved: true };
    }
    return (await apiRequest<{ data: { moved: boolean } }>(`/api/v1/crm/opportunities/${opportunityId}/advance`, { method: "POST", body: JSON.stringify({ stage, reason }) })).data;
  },
  updateOpportunity: async (opportunityId: string, input: { expected_version: number; stage: CrmStage; lost_reason?: string; next_follow_up_at?: string }) => {
    if (useDevelopmentFixtures) {
      const opportunity = crmOpportunityFixture.find((item) => item.id === opportunityId);
      if (!opportunity) throw new Error("Oportunidad no encontrada.");
      if (opportunity.version !== input.expected_version) throw new Error("La oportunidad cambió. Actualiza la ficha.");
      Object.assign(opportunity, { stage: input.stage, version: input.expected_version + 1, lostReason: input.stage === "lost" ? input.lost_reason ?? null : null, wonSource: input.stage === "won" ? "manual" : null, wonAt: input.stage === "won" ? new Date().toISOString() : null });
      return structuredClone(opportunity);
    }
    return (await apiRequest<{ data: Record<string, unknown> }>(`/api/v1/crm/opportunities/${opportunityId}`, { method: "PATCH", body: JSON.stringify(input) })).data;
  },
  /** Schedules the single next action of a sale; the current pending one is closed as `previous` (default: rescheduled). */
  createFollowup: async (input: { opportunity_id: string; due_at: string; note: string; draft_body?: string; previous?: "completed" | "cancelled" }) => {
    if (useDevelopmentFixtures) {
      const opportunity = crmOpportunityFixture.find(item => item.id === input.opportunity_id);
      const id = crypto.randomUUID(); const draftId = input.draft_body && opportunity?.threadId ? crypto.randomUUID() : null;
      if (opportunity) {
        const detail = detailFixture(opportunity.customerId);
        opportunity.nextFollowUpAt = input.due_at;
        for (const item of detail.followups)
          if (item.opportunityId === opportunity.id && item.status === "pending")
            Object.assign(item, { status: input.previous ?? "cancelled", completedAt: input.previous === "completed" ? new Date().toISOString() : null });
        detail.followups.unshift({ id, opportunityId: opportunity.id, dueAt: input.due_at, note: input.note, status: "pending", completedAt: null, version: 1 });
        if (draftId) detail.drafts.unshift({ id: draftId, threadId: opportunity.threadId!, opportunityId: opportunity.id, followupId: id, body: input.draft_body!, status: "draft", createdBy: "operator", sentAt: null, version: 1 });
      }
      return { id, version: 1, draftId };
    }
    return (await apiRequest<{ data: { id: string; version: number; draftId: string | null } }>("/api/v1/crm/followups", { method: "POST", body: JSON.stringify(input) })).data;
  },
  updateFollowup: async (followupId: string, input: { expected_version: number; status: "pending"; due_at: string; note: string } | { expected_version: number; status: "completed" | "cancelled" }) => {
    if (useDevelopmentFixtures) return { id: followupId, ...input, version: input.expected_version + 1 };
    return (await apiRequest<{ data: Record<string, unknown> }>(`/api/v1/crm/followups/${followupId}`, { method: "PATCH", body: JSON.stringify(input) })).data;
  },
  sendDraft: async (draftId: string, expectedVersion: number) => {
    if (useDevelopmentFixtures) return { messageId: crypto.randomUUID() };
    return (await apiRequest<{ data: { messageId: string } }>(`/api/v1/crm/drafts/${draftId}/send`, { method: "POST", body: JSON.stringify({ expected_version: expectedVersion }) })).data;
  },
  previewMerge: async (customerIds: [string, string], versions: [number, number]) => {
    if (useDevelopmentFixtures) return { primaryCustomerId: customerIds[0], secondaryCustomerId: customerIds[1], customers: [] };
    return (await apiRequest<{ data: { primaryCustomerId: string; secondaryCustomerId: string; customers: Array<Record<string, unknown>> } }>("/api/v1/crm/customer-merges/preview", { method: "POST", body: JSON.stringify({ customer_ids: customerIds, expected_versions: versions }) })).data;
  },
  mergeCustomers: async (customerIds: [string, string], versions: [number, number]) => {
    if (useDevelopmentFixtures) return { id: crypto.randomUUID(), primaryCustomerId: customerIds[0], secondaryCustomerId: customerIds[1], canUndo: true };
    return (await apiRequest<{ data: { id: string; primaryCustomerId: string; secondaryCustomerId: string; canUndo: boolean } }>("/api/v1/crm/customer-merges", { method: "POST", body: JSON.stringify({ customer_ids: customerIds, expected_versions: versions }) })).data;
  },
  undoMerge: async (mergeId: string) => {
    if (useDevelopmentFixtures) return { id: mergeId, status: "undone" };
    return (await apiRequest<{ data: { id: string; status: string } }>(`/api/v1/crm/customer-merges/${mergeId}/undo`, { method: "POST", body: "{}" })).data;
  },
};
